// Calendar event TYPES — the compulsory category a user picks for every calendar
// event (migration 177 adds calendar_events.event_type). The type is a SECOND,
// independent dimension from visibility (private/public/shared/sales): visibility
// says who can see it, type says what it is.
//
// Type is the single source of an event's COLOUR everywhere it renders — the grid
// chips + agenda on CalendarPage AND the top-nav NowBar pill. It also carries an
// `importance` (1-5) and a `screenMs` dwell used by the NowBar: higher-importance
// events rank first and hold the bar on screen longer ("screen time"). A version
// UPDATE (handled in NowBar, not here) outranks all of these and holds longest.
//
// Class strings are FULL literals (no interpolation) so Tailwind's JIT keeps them;
// each tone ships light + dark variants. The `notify` bundle mirrors the shape of
// NowBar's TONE map; the flat dot/soft/pill/on mirror CalendarPage's LAYER_META.
//
// KEEP THE KEYS IN SYNC with VALID_EVENT_TYPES in backend/routes/calendar.js — the
// server whitelists against that list and coerces anything unknown to 'general'.

import {
  AlertTriangle,
  Handshake,
  Sun,
  MessageSquare,
  AlarmClock,
  Users,
  Megaphone,
  ArrowUpCircle,
  StickyNote,
} from "lucide-react";

// Ordered by importance (highest first) so pickers and the legend read top-down.
export const EVENT_TYPES = [
  {
    key: "important",
    label: "Important",
    Icon: AlertTriangle,
    importance: 6,
    screenMs: 16000,
    dot: "bg-red-500",
    soft: "bg-red-500/10 text-red-600 dark:text-red-300",
    pill: "bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-200",
    on: "bg-red-500 text-white border-red-500",
    notify: {
      pill: "border-red-200/70 bg-red-50/80 hover:bg-red-50 dark:border-red-500/25 dark:bg-red-500/[0.08] dark:hover:bg-red-500/[0.12]",
      chip: "bg-red-500 text-white dark:bg-red-500/90",
      dot: "bg-red-500",
      text: "text-red-800/90 dark:text-red-200/90",
      count: "text-red-700/70 dark:text-red-300/60",
    },
  },
  {
    key: "customer_visit",
    label: "Customer Visit",
    Icon: Handshake,
    importance: 5,
    screenMs: 14000,
    dot: "bg-blue-500",
    soft: "bg-blue-500/10 text-blue-600 dark:text-blue-300",
    pill: "bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200",
    on: "bg-blue-500 text-white border-blue-500",
    notify: {
      pill: "border-blue-200/70 bg-blue-50/80 hover:bg-blue-50 dark:border-blue-500/25 dark:bg-blue-500/[0.08] dark:hover:bg-blue-500/[0.12]",
      chip: "bg-blue-500 text-white dark:bg-blue-500/90",
      dot: "bg-blue-500",
      text: "text-blue-800/90 dark:text-blue-200/90",
      count: "text-blue-700/70 dark:text-blue-300/60",
    },
  },
  {
    key: "holiday",
    label: "Holiday",
    Icon: Sun,
    importance: 4,
    screenMs: 12000,
    dot: "bg-rose-500",
    soft: "bg-rose-500/10 text-rose-600 dark:text-rose-300",
    pill: "bg-rose-500/10 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200",
    on: "bg-rose-500 text-white border-rose-500",
    notify: {
      pill: "border-rose-200/70 bg-rose-50/80 hover:bg-rose-50 dark:border-rose-500/25 dark:bg-rose-500/[0.08] dark:hover:bg-rose-500/[0.12]",
      chip: "bg-rose-500 text-white dark:bg-rose-500/90",
      dot: "bg-rose-500",
      text: "text-rose-800/90 dark:text-rose-200/90",
      count: "text-rose-700/70 dark:text-rose-300/60",
    },
  },
  {
    key: "request",
    label: "Request",
    Icon: MessageSquare,
    importance: 4,
    screenMs: 12000,
    dot: "bg-amber-500",
    soft: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    pill: "bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200",
    on: "bg-amber-500 text-white border-amber-500",
    notify: {
      pill: "border-amber-200/70 bg-amber-50/80 hover:bg-amber-50 dark:border-amber-500/25 dark:bg-amber-500/[0.08] dark:hover:bg-amber-500/[0.12]",
      chip: "bg-amber-400 text-amber-950 dark:bg-amber-400/90 dark:text-amber-950",
      dot: "bg-amber-500",
      text: "text-amber-800/90 dark:text-amber-200/90",
      count: "text-amber-700/70 dark:text-amber-300/60",
    },
  },
  {
    key: "reminder",
    label: "Reminder",
    Icon: AlarmClock,
    importance: 4,
    screenMs: 12000,
    dot: "bg-teal-500",
    soft: "bg-teal-500/10 text-teal-600 dark:text-teal-300",
    pill: "bg-teal-500/10 text-teal-700 dark:bg-teal-500/20 dark:text-teal-200",
    on: "bg-teal-500 text-white border-teal-500",
    notify: {
      pill: "border-teal-200/70 bg-teal-50/80 hover:bg-teal-50 dark:border-teal-500/25 dark:bg-teal-500/[0.08] dark:hover:bg-teal-500/[0.12]",
      chip: "bg-teal-500 text-white dark:bg-teal-500/90",
      dot: "bg-teal-500",
      text: "text-teal-800/90 dark:text-teal-200/90",
      count: "text-teal-700/70 dark:text-teal-300/60",
    },
  },
  {
    key: "meeting",
    label: "Meeting",
    Icon: Users,
    importance: 3,
    screenMs: 10000,
    dot: "bg-violet-500",
    soft: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
    pill: "bg-violet-500/10 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200",
    on: "bg-violet-500 text-white border-violet-500",
    notify: {
      pill: "border-violet-200/70 bg-violet-50/80 hover:bg-violet-50 dark:border-violet-500/25 dark:bg-violet-500/[0.08] dark:hover:bg-violet-500/[0.12]",
      chip: "bg-violet-500 text-white dark:bg-violet-500/90",
      dot: "bg-violet-500",
      text: "text-violet-800/90 dark:text-violet-200/90",
      count: "text-violet-700/70 dark:text-violet-300/60",
    },
  },
  {
    key: "news",
    label: "News",
    Icon: Megaphone,
    importance: 2,
    screenMs: 9000,
    dot: "bg-indigo-500",
    soft: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300",
    pill: "bg-indigo-500/10 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200",
    on: "bg-indigo-500 text-white border-indigo-500",
    notify: {
      pill: "border-indigo-200/70 bg-indigo-50/80 hover:bg-indigo-50 dark:border-indigo-500/25 dark:bg-indigo-500/[0.08] dark:hover:bg-indigo-500/[0.12]",
      chip: "bg-indigo-500 text-white dark:bg-indigo-500/90",
      dot: "bg-indigo-500",
      text: "text-indigo-800/90 dark:text-indigo-200/90",
      count: "text-indigo-700/70 dark:text-indigo-300/60",
    },
  },
  {
    key: "update",
    label: "Update",
    Icon: ArrowUpCircle,
    importance: 2,
    screenMs: 9000,
    dot: "bg-orange-500",
    soft: "bg-orange-500/10 text-orange-600 dark:text-orange-300",
    pill: "bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200",
    on: "bg-orange-500 text-white border-orange-500",
    notify: {
      pill: "border-orange-200/70 bg-orange-50/80 hover:bg-orange-50 dark:border-orange-500/25 dark:bg-orange-500/[0.08] dark:hover:bg-orange-500/[0.12]",
      chip: "bg-orange-500 text-white dark:bg-orange-500/90",
      dot: "bg-orange-500",
      text: "text-orange-800/90 dark:text-orange-200/90",
      count: "text-orange-700/70 dark:text-orange-300/60",
    },
  },
  {
    key: "general",
    label: "General",
    Icon: StickyNote,
    importance: 1,
    screenMs: 7000,
    dot: "bg-slate-500",
    soft: "bg-slate-500/10 text-slate-600 dark:text-slate-300",
    pill: "bg-slate-500/10 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200",
    on: "bg-slate-500 text-white border-slate-500",
    notify: {
      pill: "border-slate-200/70 bg-slate-50/80 hover:bg-slate-50 dark:border-slate-500/25 dark:bg-slate-500/[0.08] dark:hover:bg-slate-500/[0.12]",
      chip: "bg-slate-500 text-white dark:bg-slate-500/90",
      dot: "bg-slate-500",
      text: "text-slate-700 dark:text-slate-300",
      count: "text-slate-600/70 dark:text-slate-400/60",
    },
  },
];

// Neutral fallback for events created before mig 177 (or an unknown key).
export const DEFAULT_EVENT_TYPE = "general";

const EVENT_TYPE_MAP = EVENT_TYPES.reduce((m, t) => ((m[t.key] = t), m), {});

// Meta for a stored event_type key — always returns a valid entry.
export const eventTypeMetaOf = (key) =>
  EVENT_TYPE_MAP[key] || EVENT_TYPE_MAP[DEFAULT_EVENT_TYPE];
